/**
 * Personenansicht (`screen-access-user`, Spec 05b, „Personenansicht"). Aus
 * `access-user-row-<id>` in `routes/Access.tsx` erreichbar — aber auch direkt
 * über die URL: Der `person`-Block von `GET /access/users/{id}/grants` trägt
 * Name, E-Mail, Art und „eingeladen am" mit, der Bildschirm lädt dafür nichts
 * nach (Spec: „funktioniert deshalb auch dann, wenn man seine Adresse direkt
 * aufruft").
 *
 * `person: null` ist die Antwort auf einen externen Aufrufer (`ARCHITECTURE.md`
 * 7, „extern entdeckt nichts") — kein 403, ein leerer Bildschirm. Praktisch
 * unerreichbar über die Oberfläche (`account-access` erscheint nur intern),
 * aber die Adresse selbst bleibt aufrufbar.
 *
 * Spec 25, Oberfläche D: Der Kopf trägt zusätzlich die Kennsätze aus B
 * (`access-user-full-access`/`access-user-disabled`, ohne `<id>`) und bei
 * Sperre den Hinweis „Gesperrt: kein Zugriff, keine Anmeldung." **Nur bei
 * `is_admin`** (aus `GET /access/me`, hier über `useMe`) erscheint die
 * primäre Aktion `access-person-edit` „Bearbeiten". **D3 (Entscheidung
 * Lukas):** Ab 768px steht `access-person-disable`/`-enable` (geteilte
 * Bauform `PersonDisableAction`, mit eigener Rückfrage) als sekundärer Knopf
 * direkt daneben — unter 768px liegt derselbe Knopf statt hier im
 * Bearbeiten-Sheet (`AccessPersonSheet`). Für die eigene Person fehlt die
 * Zeile ganz (Server sagt 409 `self_lockout`, der Client zeigt keinen Knopf,
 * den er nicht drücken darf).
 */

import { Link, useParams } from '@tanstack/react-router'
import { useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { formatInstantInBerlin } from '@/lib/date'
import { useIsDesktopViewport } from '@/lib/viewport'
import { useMe, useUserGrants } from '@/features/access/api'
import type { PersonHead, UserGrantItem } from '@/features/access/api'
import { AccessPersonSheet } from '@/features/access/AccessPersonSheet'
import { DisabledBadge, FullAccessBadge } from '@/features/access/PersonBadges'
import { PersonDisableAction } from '@/features/access/PersonDisableAction'

export function AccessUser() {
  const { userId } = useParams({ from: '/zugriff/$userId' })
  const query = useUserGrants(userId)
  const meQuery = useMe()

  return (
    <section aria-labelledby="access-user-heading" data-testid="screen-access-user">
      <header className="border-border flex min-h-(--row-h) items-center gap-3 border-b px-(--page-x) py-1">
        {/* `inline-flex items-center` (D-W3, `15-design-reviewer.md`, wie
         * `PaymentAllocations.tsx`): Als Flex-Kind wird der `<a>` von
         * `global.css:72` blockifiziert, `min-height: var(--tap-min)`
         * schiebt seinen Text sonst an den oberen Rand des 44-px-Kastens
         * statt ihn zu zentrieren. */}
        <Link
          to="/zugriff"
          data-testid="access-user-back"
          className="text-accent text-label inline-flex min-h-(--tap-min) items-center hover:underline"
        >
          ← Zugriffsverwaltung
        </Link>
      </header>

      {query.isPending && <SkeletonRows testId="access-user-grants-loading" rows={4} />}

      {query.isError && (
        <ErrorState
          testId="access-user-grants-error"
          retryTestId="access-user-grants-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {/* `person === null` (externer Aufrufer, praktisch unerreichbar über
       * die Oberfläche, s. Dateikopf) hat keinen eigenen Leerzustand in der
       * Spec — nur „Drei Zustände: … `access-user-grants-empty` (‚Diese
       * Person hat noch keinen Zugriff.' mit der Aufforderung …)" (Spec,
       * „Personenansicht"). Ein erfundener zweiter Text („Kein Zugriff." ohne
       * Aufforderung) verstieß gegen „Leer — eine Aufforderung, keine
       * Meldung" (W4, `15-design-reviewer.md`) — hier bleibt es beim
       * spezifizierten Text statt einer zweiten, unbelegten Fassung. */}
      {query.isSuccess && query.data.person === null && (
        <EmptyState
          testId="access-user-grants-empty"
          title="Diese Person hat noch keinen Zugriff."
          hint="Gib ihr Zugriff an einem Objekt."
        />
      )}

      {query.isSuccess && query.data.person !== null && (
        <>
          <PersonHeadBlock person={query.data.person} isAdmin={meQuery.data?.is_admin === true} selfId={meQuery.data?.id} />

          {query.data.items.length === 0 ? (
            <EmptyState
              testId="access-user-grants-empty"
              title="Diese Person hat noch keinen Zugriff."
              hint="Gib ihr Zugriff an einem Objekt."
            />
          ) : (
            <>
              <ul data-testid="access-user-grants">
                {query.data.items.map((grant) => (
                  <UserGrantRow key={grant.entity_id} grant={grant} />
                ))}
              </ul>
              <p className="text-label text-muted px-(--page-x) py-2">… und alles, was darunter liegt.</p>
            </>
          )}
        </>
      )}
    </section>
  )
}

interface PersonHeadBlockProps {
  readonly person: PersonHead
  /** `is_admin` des **Aufrufers** (aus `GET /access/me`) — nicht der
   * betrachteten Person. Bequemlichkeit, kein Schutz (Fachregel 6). */
  readonly isAdmin: boolean
  readonly selfId: string | undefined
}

/** Kopf der Personenansicht mit den Admin-Aktionen (Spec 25, Oberfläche D).
 * Eigene Komponente, weil sie eigene Sheet-/Rückfrage-Zustände braucht, die
 * `AccessUser` sonst für einen `person === null`-Aufruf unnötig mitschleppte. */
function PersonHeadBlock({ person, isAdmin, selfId }: PersonHeadBlockProps) {
  const isDesktop = useIsDesktopViewport()
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [actionError, setActionError] = useState<string | undefined>(undefined)

  const isDisabled = person.state.value === 'disabled'
  // D3 (Entscheidung Lukas): Der Knopf steht ab 768px hier, neben
  // „Bearbeiten"; unter 768px liegt er stattdessen im Bearbeiten-Sheet
  // (`AccessPersonSheet`, Auflage 3) — nie an beiden Stellen zugleich.
  const showDisableHere = isDesktop && person.id !== selfId

  return (
    <>
      <div
        data-testid="access-user-head"
        className="border-border flex flex-col gap-2 border-b px-(--page-x) py-3 md:flex-row md:items-start md:justify-between"
      >
        <div className="flex flex-col gap-1">
          <h1 id="access-user-heading" className="text-page font-(--font-weight-page) text-text flex flex-wrap items-center gap-2">
            {person.display_name ?? person.email}
            {person.full_access && <FullAccessBadge testId="access-user-full-access" />}
            {isDisabled && <DisabledBadge testId="access-user-disabled" label={person.state.label} />}
          </h1>
          <p className="text-label text-muted">{person.email}</p>
          <p className="text-label text-muted">
            {person.person_kind.label}
            {person.invited_at !== null && ` · angelegt am ${formatInstantInBerlin(person.invited_at)}`}
          </p>
          {isDisabled && <p className="text-label text-muted">Gesperrt: kein Zugriff, keine Anmeldung.</p>}
          {actionError !== undefined && (
            <p role="alert" className="text-label text-status-overdue">
              {actionError}
            </p>
          )}
        </div>

        {isAdmin && (
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              data-testid="access-person-edit"
              onClick={() => {
                setIsEditOpen(true)
              }}
              className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
            >
              Bearbeiten
            </button>
            {showDisableHere && (
              <PersonDisableAction personId={person.id} isDisabled={isDisabled} onError={setActionError} />
            )}
          </div>
        )}
      </div>

      {isAdmin && (
        <AccessPersonSheet
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          person={person}
          selfId={selfId ?? ''}
        />
      )}
    </>
  )
}

function UserGrantRow({ grant }: { readonly grant: UserGrantItem }) {
  const isExpired = grant.state.value !== 'active'

  return (
    <li className={`border-border border-b last:border-b-0 ${isExpired ? 'opacity-60' : ''}`}>
      {/* Jede Zeile führt per Klick auf das Objekt (Spec: „Entzogen wird
       * dort, im `access-sheet` aus 05a — nicht hier"). */}
      <Link
        to="/objekt/$entityId"
        params={{ entityId: grant.entity_id }}
        search={{ tab: undefined }}
        data-testid={`access-user-grant-${grant.entity_id}`}
        className="hover:bg-bg flex min-h-(--row-h) flex-col gap-1 px-(--page-x) py-2 transition-colors duration-(--duration-fast) ease-(--ease-out) md:flex-row md:items-center md:gap-3"
      >
        {/* `entity_label` ist der Name des Objekts, `path_label` nur die
         * Vorfahren darüber (`shared/hierarchy.py::path_label_expr`, leer an
         * einer Wurzel) — dieselbe Aufteilung wie `MoveTarget` in
         * `MoveSheet.tsx`. Nur `path_label` zu zeigen ließ zwei Gebäude
         * desselben Grundstücks als dieselbe Zeile aussehen. */}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-body text-text truncate">{grant.entity_label}</span>
          {grant.path_label !== '' && <span className="text-label text-muted truncate">{grant.path_label}</span>}
        </span>
        <span className="text-label text-muted shrink-0">{grant.role.label}</span>
        <span className="text-label text-muted shrink-0">{grant.state.label}</span>
        {grant.expires_at !== null && (
          <span className="text-label text-muted shrink-0">
            Letzter Tag mit Zugriff: {formatInstantInBerlin(grant.expires_at)}
          </span>
        )}
      </Link>
    </li>
  )
}
